<template>
  <table class="emd-table">
    <thead>
      <tr>
        <th
          class="emd-text"
          v-for="(field, index) in fields"
          :key="index"
        >
          {{ $t(field.label) }}
          <emd-tippy
            v-if="field.info"
            :data-template="field.id"
            :id="field.id"
            class="eu-ml_1"
            :type="'html'"
            position="bottom"
          >
            <i
              :class="'emd-text _color_subtle'"
              class="uil uil-info-circle"
            />
            <emd-tippy-template-HTML :id="field.id">
              <slot />
            </emd-tippy-template-HTML>
          </emd-tippy>
        </th>
        <th v-if="showDetailsOrActions"></th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="(item, itemIndex) in items"
        :key="itemIndex"
      >
        <td
          v-for="(field, index) in fields"
          :key="index"
          :data-label="$t(field.label)"
          :class="{ 'eu-align_right': isActionDelete(field.type) }"
        >
          <span
            v-if="field.type === 'link'"
            @click="eventCaller(field.id)"
          >
            <router-link
              :data-testid="`link-show-${itemIndex}`"
              :to="{
                name: field.link.to,
                params: {
                  id: getField(item, field.link.params.id),
                  tab: field.link.params.tab
                },
                query: getQuery(item, field.link.query)
              }"
              class="emd-link"
              >{{ getField(item, field.id) || '-' }}</router-link
            >
          </span>

          <span
            v-else-if="field.type === 'text'"
            :data-testid="`text-show-${itemIndex}`"
            >{{ transformText(item, field) || '-' }}
          </span>

          <span
            v-else-if="field.type === 'conditional'"
            :data-testid="`text-show-${itemIndex}`"
            >{{ getConditionalField(item, field.id) || '-' }}</span
          >

          <span
            v-else-if="field.type === 'status'"
            class="emd-badge"
            :data-testid="`status-badge-${itemIndex}`"
            v-bind:class="statusClass(getField(item, field.key || field.id))"
            >{{ transformText(item, field) || '-' }}</span
          >

          <span v-else-if="field.type === 'interval'">
            {{ $t('plan.list.each') }} {{ item.interval_count }}
            {{ pluralIntervals(item.interval_count, item.interval) }}</span
          >

          <span
            v-else-if="field.type === 'date'"
            :data-testid="`date-show-${itemIndex}`"
            >{{ getField(item, field.id) | dateFormat(field.format) }}</span
          >

          <span
            v-else-if="field.type === 'url'"
            class="url-column"
          >
            <a
              class="url-color"
              target="_blank"
              rel="noopener noreferrer"
              :href="$sanitize(item.url)"
              :data-testid="`url-show-${itemIndex}`"
            >
              <span
                v-if="copiedValue"
                class="url-copied"
              >
                {{ $t('table.copied', [computedValueCopied]) || '-' }}
              </span>
              <span v-else-if="field.text"> {{ field.text || '-' }} </span>
              <span v-else> {{ getField(item, field.id) || '-' }} </span>
            </a>
            <button
              v-if="field.showCopy"
              class="emd-action-button"
              @click="handleCopy(getField(item, field.id))"
            >
              <emerald-icon
                icon="copy"
                data-testid="button-copy"
              />
            </button>
          </span>

          <span
            v-else-if="field.type === 'label-tooltip'"
            :data-testid="`label-tooltip-show-${itemIndex}`"
          >
            {{ getField(item, field.id) || '-' }}

            <template v-if="item?.tooltip">
              <emd-tippy
                v-if="Array.isArray(item.tooltip)"
                :id="field.id + '_' + itemIndex"
                :data-template="field.id + '_' + itemIndex"
                class="eu-ml_1"
                type="html"
                position="bottom"
              >
                <i class="uil uil-info-circle emd-text _color_primary"></i>
                <emd-tippy-template-HTML :id="field.id + '_' + itemIndex">
                  <div
                    v-for="(item, index) in item.tooltip"
                    :key="index"
                  >
                    {{ item }}
                  </div>
                </emd-tippy-template-HTML>
              </emd-tippy>

              <emd-tippy
                v-else
                :id="field.id + '_' + itemIndex"
                :content="item.tooltip"
                class="eu-ml_1"
                type="simple"
                position="bottom"
              >
                <i class="uil uil-info-circle emd-text _color_primary"></i>
              </emd-tippy>
            </template>
          </span>

          <emd-button
            v-if="isActionDelete(field.type)"
            color="danger"
            type="outline"
            :text="$t('general.delete')"
            @click="$emit('delete', item)"
          />
        </td>

        <td
          class="eu-align_right"
          v-if="toDetail"
          @click="eventCaller('details')"
        >
          <router-link
            :to="{
              name: toDetail.name,
              params: {
                id: item[toDetail.params.id],
                tab: toDetail.params.tab
              },
              query: getQuery(item, toDetail.query)
            }"
            :data-testid="`detail-show-${itemIndex}`"
            class="emd-btn-ghost"
          >
            <i class="uil uil-arrow-to-right" />
          </router-link>
        </td>

        <td
          class="eu-align_right"
          v-if="actions.length"
        >
          <ul class="emd-list _space_base _inline">
            <template v-for="(action, index) in actions">
              <li
                :key="index"
                v-if="item[action.field]"
              >
                <a
                  v-if="action.type === 'function'"
                  href="javascript:void(0)"
                  class="emd-btn-ghost"
                  :title="action.title"
                  v-on:click="action.fnAction(item)"
                >
                  <i :class="`uil uil-${action.icon}`" />
                </a>
                <router-link
                  v-else-if="action.type === 'router-link'"
                  :to="{
                    name: action.name,
                    params: {
                      id: item[action.params.id],
                      tab: action.params.tab
                    },
                    query: getQuery(item, action.query)
                  }"
                  class="emd-btn-ghost eu-mb_3"
                >
                  <i :class="`uil uil-${action.icon}`" />
                </router-link>
              </li>
            </template>
          </ul>
        </td>
      </tr>
    </tbody>
  </table>
</template>
<script>
import sendEvent from '@utils/analiticaEventCaller.utils'
import { statusClass, pluralIntervals } from '@/filters'
import {
  EmdTippy,
  EmdTippyTemplateHTML,
  EmdButton,
  EmeraldIcon
} from 'emerald-vue/src/components'

export default {
  name: 'Table',
  components: {
    EmdTippy,
    EmdTippyTemplateHTML,
    EmdButton,
    EmeraldIcon
  },
  props: {
    fields: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    toDetail: [Object, Boolean],
    actions: {
      required: false,
      type: Array,
      default () {
        return []
      }
    },
    eventProps: Object
  },
  data () {
    return {
      statusClass: statusClass,
      pluralIntervals: pluralIntervals,
      copiedValue: undefined
    }
  },

  computed: {
    computedValueCopied () {
      if (!this.copiedValue) return undefined
      const pos = this.copiedValue.search('pl_')
      return this.copiedValue.slice(pos, pos + 12)
    },
    showDetailsOrActions () {
      return this.toDetail || this.actions.length
    }
  },

  methods: {
    isActionDelete (type) {
      return type === 'action_delete'
    },

    getField (item, field) {
      if (field.includes('.')) {
        const fieldArray = field.split('.')
        let value
        for (let i = 0; i < fieldArray.length; i++) {
          if (value) {
            value = value[fieldArray[i]]
          } else {
            value = item[fieldArray[i]]
          }
        }
        return value
      }
      return item[field]
    },
    getConditionalField (item, field) {
      const options = field.split(';')
      return this.getField(item, options[0]) || this.getField(item, options[1])
    },
    getQuery (item, fields) {
      if (!fields) return {}
      const obj = {}
      fields.forEach(field => {
        obj[field] = this.getField(item, field)
      })
      return obj
    },
    transformText (item, field) {
      return field.transform
        ? field.transform(this.getField(item, field.id), item)
        : this.getField(item, field.id) || '-'
    },
    handleCopy (value) {
      this.$copyText(value)
        .then(() => {
          this.copiedValue = value
        })
        .finally(() => {
          setTimeout(() => (this.copiedValue = undefined), 3000)
        })
    },
    eventCaller (eventContextName) {
      if (
        !this.eventProps?.amplitudeInstance ||
        !this.eventProps[eventContextName]?.eventFunctionName
      )
        return

      sendEvent({
        instanceName: this.eventProps.amplitudeInstance,
        featureName: this.eventProps[eventContextName].feature,
        functionName: this.eventProps[eventContextName].eventFunctionName
      })
    }
  }
}
</script>

<i18n>
  {
    "pt-BR": {
      "table": {
        "copied": "Copiado: {0}"
      }
    },
    "en-US": {
      "table": {
        "copied": "Copied: {0}"
      }
    }
  }
</i18n>

<style scoped>
.url-column {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.url-copied {
  color: var(--brand--le--primary-color--base, var(--color-green));
}

.url-color {
  color: var(--primrias-purple-100, #146441);
}
</style>
